import { useMemo, useRef, useState } from 'react';
import { Button } from '@web/components/Button';
import { useNow } from '@web/hooks/useNow';
import { cn } from '@web/lib/cn';
import { windowExpiresAt, windowStatus } from '@web/lib/time';
import {
  cannedResponsesSchema,
  SETTING_KEYS,
  templatesSchema,
  type CannedResponse,
} from '@web/features/settings/schema';
import { useSetting } from '@web/features/settings/useSettings';
import { useSendMessage, useSendNote } from './mutations';

type Tab = 'reply' | 'note';

/**
 * The composer.
 *
 * Three things it must never get wrong:
 *
 *  1. A note must not look like a reply. Different tab, different border,
 *     different colour, and the words "never sent to the candidate" sitting
 *     under the field the whole time it is selected.
 *  2. Once the 24-hour service window has closed, free text is not deliverable
 *     and pretending otherwise wastes an operator's time and produces a 502.
 *     The reply field is replaced — not merely disabled — by the template
 *     picker, because the available action genuinely changed.
 *  3. Nothing is optimistic. The bubble appears when WhatsApp has taken it.
 */
export function Composer({
  conversationId,
  lastInboundAt,
  replyBlockedReason,
  onSent,
}: {
  conversationId: string;
  lastInboundAt: string | null;
  /**
   * Why this operator cannot reply — not holding the conversation, usually.
   * Notes are never blocked: writing down what you know for the person who does
   * hold it is always allowed, and always internal.
   */
  replyBlockedReason?: string;
  onSent?: () => void;
}) {
  const now = useNow();
  const [tab, setTab] = useState<Tab>('reply');
  const [text, setText] = useState('');
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const send = useSendMessage(conversationId);
  const note = useSendNote(conversationId);
  const canned = useSetting(SETTING_KEYS.canned, cannedResponsesSchema, [] as CannedResponse[]);
  const templates = useSetting(SETTING_KEYS.templates, templatesSchema, []);

  const status = windowStatus(windowExpiresAt(lastInboundAt), now);
  const expired = status === 'expired' || status === 'never-opened';

  // `/` at the start of an empty-ish field opens the canned list, filtered as
  // they keep typing. Nothing pops up mid-sentence.
  const slashQuery = text.startsWith('/') ? text.slice(1).toLowerCase() : null;
  const suggestions = useMemo(() => {
    if (slashQuery === null) return [];
    const all = canned.data?.value ?? [];
    return all
      .filter(
        (item) =>
          item.shortcut.toLowerCase().includes(slashQuery) ||
          item.body.toLowerCase().includes(slashQuery),
      )
      .slice(0, 6);
  }, [slashQuery, canned.data]);

  const pending = send.isPending || note.isPending;
  const isNote = tab === 'note';
  const blocked = !isNote && replyBlockedReason !== undefined;

  function submit(): void {
    const body = text.trim();
    if (body === '' || pending || blocked) return;
    const mutation = isNote ? note : send;
    mutation.mutate(
      { body },
      {
        onSuccess: () => {
          setText('');
          onSent?.();
        },
      },
    );
  }

  return (
    <div className="border-t border-rule bg-panel">
      <div className="flex items-stretch border-b border-rule">
        {(['reply', 'note'] as const).map((value) => (
          <button
            key={value}
            type="button"
            role="tab"
            aria-selected={tab === value}
            onClick={() => {
              setTab(value);
              inputRef.current?.focus();
            }}
            className={cn(
              'min-h-9 px-3 font-mono text-[11px] tracking-[0.1em] uppercase',
              tab === value
                ? value === 'note'
                  ? 'border-b-2 border-warm text-warm'
                  : 'border-b-2 border-ink text-ink'
                : 'border-b-2 border-transparent text-ink-faint hover:text-ink-dim',
            )}
          >
            {value}
          </button>
        ))}

        <div className="flex flex-1 items-center justify-end px-3">
          {isNote ? (
            <span className="font-mono text-[10px] tracking-[0.06em] text-warm">
              NEVER SENT TO THE CANDIDATE
            </span>
          ) : expired ? (
            <span className="font-mono text-[10px] tracking-[0.06em] text-signal">
              WINDOW CLOSED · TEMPLATES ONLY
            </span>
          ) : null}
        </div>
      </div>

      {blocked && (
        <p className="border-b border-rule px-3 py-2 text-xs text-ink-dim">{replyBlockedReason}</p>
      )}

      {!isNote && expired ? (
        <TemplatePicker
          templates={templates.data?.value ?? []}
          disabled={blocked || pending}
          onPick={(body) => {
            setText(body);
            setTab('reply');
            inputRef.current?.focus();
          }}
          onSend={(body) => {
            send.mutate({ body }, { onSuccess: () => onSent?.() });
          }}
        />
      ) : (
        <div className="relative">
          {suggestions.length > 0 && (
            <ul className="absolute bottom-full left-0 z-10 max-h-56 w-full overflow-auto border-t border-rule bg-raised">
              {suggestions.map((item) => (
                <li key={item.id}>
                  <button
                    type="button"
                    className="flex w-full min-h-11 flex-col items-start gap-0.5 border-b border-rule px-3 py-2 text-left hover:bg-hover"
                    onClick={() => {
                      setText(item.body);
                      inputRef.current?.focus();
                    }}
                  >
                    <span className="font-mono text-[10px] tracking-[0.08em] text-ink-faint">
                      /{item.shortcut}
                    </span>
                    <span className="line-clamp-2 text-xs text-ink-dim">{item.body}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}

          <div className="flex items-end gap-2 p-2">
            <textarea
              ref={inputRef}
              value={text}
              disabled={blocked}
              onChange={(event) => {
                setText(event.target.value);
              }}
              onKeyDown={(event) => {
                // Enter sends, Shift+Enter breaks the line. On a phone the
                // on-screen keyboard's return key inserts a newline instead —
                // the send button is the deliberate action there.
                if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) {
                  event.preventDefault();
                  submit();
                }
              }}
              rows={2}
              placeholder={
                isNote ? 'Internal note for colleagues…' : 'Reply — type / for a canned response'
              }
              aria-label={isNote ? 'Internal note' : 'Reply to candidate'}
              className={cn(
                'max-h-40 min-h-[44px] flex-1 resize-y border bg-ground px-2 py-1.5 text-ink placeholder:text-ink-faint',
                isNote ? 'border-warm' : 'border-rule-strong',
              )}
            />
            <Button
              tone={isNote ? 'quiet' : 'primary'}
              size="touch"
              disabled={blocked || pending || text.trim() === ''}
              onClick={submit}
            >
              {pending ? 'Sending…' : isNote ? 'Save note' : 'Send'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * What is left when the window has closed.
 *
 * The templates come from the `wa.templates` setting. NOTE, and this is in the
 * report: the server has no template-send endpoint yet, so "Send" posts the
 * rendered body through the ordinary message route. If WhatsApp refuses it the
 * operator sees the refusal rather than a silent failure — but the correct fix
 * is a `POST /api/conversations/:id/template` that sends a real template.
 */
function TemplatePicker({
  templates,
  disabled,
  onPick,
  onSend,
}: {
  templates: { id: string; name: string; language: string; body: string }[];
  disabled: boolean;
  onPick: (body: string) => void;
  onSend: (body: string) => void;
}) {
  const [selected, setSelected] = useState<string | null>(null);
  const chosen = templates.find((template) => template.id === selected);

  if (templates.length === 0) {
    return (
      <div className="p-3 text-xs text-ink-dim">
        The 24-hour window has closed, so only an approved template can be delivered. No templates
        are configured — an admin can add them under Settings.
      </div>
    );
  }

  return (
    <div className="p-2">
      <ul className="max-h-40 overflow-auto border border-rule">
        {templates.map((template) => (
          <li key={template.id}>
            <button
              type="button"
              aria-pressed={selected === template.id}
              onClick={() => {
                setSelected(template.id);
                onPick(template.body);
              }}
              className={cn(
                'flex min-h-11 w-full flex-col items-start gap-0.5 border-b border-rule px-3 py-2 text-left',
                selected === template.id ? 'bg-hover' : 'hover:bg-hover',
              )}
            >
              <span className="font-mono text-[10px] tracking-[0.08em] text-ink-faint">
                {template.name} · {template.language}
              </span>
              <span className="line-clamp-2 text-xs text-ink-dim">{template.body}</span>
            </button>
          </li>
        ))}
      </ul>

      <div className="mt-2 flex justify-end">
        <Button
          tone="primary"
          size="touch"
          disabled={disabled || chosen === undefined}
          onClick={() => {
            if (chosen !== undefined) onSend(chosen.body);
          }}
        >
          Send template
        </Button>
      </div>
    </div>
  );
}
